import { useEffect, useRef, useState } from 'react';

import { useGlobalContext } from '../../../context/global';
import type { GeoPosition } from '../../../hooks/utils/geolocation';
import { appendLocationFields, resolveUploadCoords } from '../../../lib/upload-location';
import { TypeButton } from '../../utils/button';
import { isValidResultUrl, validResultUrls } from '../../utils/helper';
import { Hvc } from '../../utils/hooks';
import type { IFileUploadStatus } from '../../utils/multiple-upload-document';
import { fileKey, MultipleMediaUploadComponent } from '../../utils/multiple-upload-document';
import { AlertSVG, CheckSVG, LightbulbSVG, UploadIconSVG } from '../../utils/svgs';

const MAX_UPLOAD_RETRIES = 2;

const UploadResultStage = () => {
	const {
		global: {
			state: { files, isUpdate, isPreview: preview, uploadedFiles },
			updateState,
		},
		rsProps,
		actions,
		onStage,
		locationTracking,
	} = useGlobalContext();

	// A result is only "uploaded" once EVERY selected sheet has a validated URL.
	// We deliberately compare against the validated subset (never the raw array
	// length) so an empty/garbage URI can never open the proceed gate.
	const isUploaded = files.length > 0 && validResultUrls(uploadedFiles).length === files.length;

	// Per-file upload state, keyed by a stable `name-size` id so add/remove mid-flight
	// never reindexes the wrong card. `statusesRef` mirrors it for synchronous reads
	// inside the sequential upload loop (React state batches are not yet flushed there).
	const [fileStatuses, setFileStatuses] = useState<Record<string, IFileUploadStatus>>({});
	const statusesRef = useRef<Record<string, IFileUploadStatus>>({});

	// Whole-batch busy flag — drives the primary button and the re-entrancy guard.
	const [batchUploading, setBatchUploading] = useState(false);
	// Resolving the GPS fix happens before the request, so it has its own busy flag.
	const [locating, setLocating] = useState(false);
	// Persistent (not toast-only) "location required" gate.
	const [gpsBlocked, setGpsBlocked] = useState(false);

	// Reset transient status when the panel is cleared (it stays mounted behind other
	// panels, so we prune here rather than on unmount).
	useEffect(() => {
		if (!files.length) {
			statusesRef.current = {};
			setFileStatuses({});
		}
	}, [files.length]);

	const setStatus = (id: string, patch: Partial<IFileUploadStatus>) => {
		const prev: IFileUploadStatus = statusesRef.current[id] ?? { phase: 'pending', progress: 0 };
		statusesRef.current = { ...statusesRef.current, [id]: { ...prev, ...patch } };
		setFileStatuses(statusesRef.current);
	};

	const handlePreviewFile = (index: number) => {
		rsProps?.addRightSectionHistory();
		rsProps?.callSection({
			action: 'view',
			component: 'preview-media-file',
			title: 'Preview Select File',
			data: {
				file: files[index],
			},
		});
	};

	// Uploads a single sheet with backoff retry (field agents upload over flaky mobile
	// networks). Resolves to the validated URL on success, or `null` once retries are
	// exhausted / the service returns an unusable link. Never throws.
	const uploadOneFile = (file: File, coords: GeoPosition) =>
		new Promise<string | null>((resolve) => {
			const id = fileKey(file);
			const attempt = (n: number) => {
				setStatus(id, { phase: 'uploading', progress: 0, error: undefined });
				const fd = new FormData();
				fd.append('upload', file);
				appendLocationFields(fd, coords);
				actions?.upload_Result({
					data: fd,
					onUploadProgress: (pct) => setStatus(id, { phase: 'uploading', progress: pct }),
					onSuccess: (res) => {
						const uri = res?.data?.uri;
						if (isValidResultUrl(uri)) {
							setStatus(id, { phase: 'success', progress: 100, url: uri.trim(), error: undefined });
							resolve(uri.trim());
							return;
						}
						// 2xx but no usable URL — treat as a (retryable) failure so we never
						// store an invalid link or advance past this step.
						if (n < MAX_UPLOAD_RETRIES) {
							setStatus(id, { phase: 'uploading', progress: 0, error: `Retrying (${n + 1}/${MAX_UPLOAD_RETRIES})…` });
							setTimeout(() => attempt(n + 1), 1000 * (n + 1));
						} else {
							setStatus(id, { phase: 'error', progress: 0, error: 'Server did not return a valid result link.' });
							resolve(null);
						}
					},
					onFailure: () => {
						if (n < MAX_UPLOAD_RETRIES) {
							setStatus(id, { phase: 'uploading', progress: 0, error: `Retrying (${n + 1}/${MAX_UPLOAD_RETRIES})…` });
							setTimeout(() => attempt(n + 1), 1000 * (n + 1));
						} else {
							setStatus(id, { phase: 'error', progress: 0, error: 'Upload failed. Check your connection and retry.' });
							resolve(null);
						}
					},
				});
			};
			attempt(0);
		});

	// Resolves a file's already-validated URL: prefer the live per-file status, then
	// fall back to the persisted `uploadedFiles` (index-aligned) — the latter survives
	// the stage unmounting/remounting when navigating between steps.
	const resolvedUrlFor = (file: File, index: number): string | undefined => {
		const live = statusesRef.current[fileKey(file)]?.url;
		if (isValidResultUrl(live)) return live.trim();
		const persisted = uploadedFiles?.[index];
		return isValidResultUrl(persisted) ? persisted.trim() : undefined;
	};

	// Rebuilds the canonical `uploadedFiles` array (in file order) from the validated
	// per-file results and advances ONLY when every sheet produced a valid URL.
	const finalizeBatch = () => {
		const urls = files.map((f, i) => resolvedUrlFor(f, i)).filter(isValidResultUrl);
		updateState('uploadedFiles', urls);
		if (urls.length === files.length && files.length > 0) {
			updateState('uploadedAt', new Date().toLocaleString());
			onStage('Upload Result Status');
		} else {
			actions?.notificationGlobal('Some sheets failed to upload. Retry the highlighted files before continuing.', false);
		}
	};

	const withCoords = async (run: (coords: GeoPosition) => Promise<void>) => {
		// A fresh GPS fix (falling back to the last tracked position) geo-stamps each
		// result sheet. No coordinates → block, as required for result integrity.
		setLocating(true);
		const coords = await resolveUploadCoords(locationTracking);
		setLocating(false);
		if (coords?.latitude == null || coords.longitude == null) {
			setGpsBlocked(true);
			actions?.notificationGlobal('Location required — enable GPS to upload results.', false);
			return;
		}
		setGpsBlocked(false);
		await run(coords);
	};

	const onUploadDocument = async () => {
		if (batchUploading || locating || !files.length) return; // re-entrancy / double-submit guard
		setBatchUploading(true);
		await withCoords(async (coords) => {
			for (let i = 0; i < files.length; i++) {
				// Skip sheets already uploaded successfully (lets the button double as "retry the rest").
				if (resolvedUrlFor(files[i], i)) continue;
				await uploadOneFile(files[i], coords);
			}
			finalizeBatch();
		});
		setBatchUploading(false);
	};

	const onRetryFile = async (index: number) => {
		const file = files[index];
		if (!file || batchUploading || locating) return;
		setBatchUploading(true);
		await withCoords(async (coords) => {
			await uploadOneFile(file, coords);
			finalizeBatch();
		});
		setBatchUploading(false);
	};

	const busy = batchUploading || locating;

	return (
		<div className='f-column-24 w-100'>
			{/* Photo quality guidance */}
			<Hvc
				removeDOM
				view={!files.length}
			>
				<div className='upload-result__guidance'>
					<div className='upload-result__guidance-header'>
						<LightbulbSVG color='var(--info-500)' />
						<h4>Tips for a clear result photo</h4>
					</div>
					<ul className='upload-result__guidance-list'>
						<li className='upload-result__guidance-item'>
							<CheckSVG
								color='var(--accent-500)'
								width='14'
								height='14'
							/>
							<span>Place the result sheet on a flat surface</span>
						</li>
						<li className='upload-result__guidance-item'>
							<CheckSVG
								color='var(--accent-500)'
								width='14'
								height='14'
							/>
							<span>Ensure good lighting with no shadows</span>
						</li>
						<li className='upload-result__guidance-item'>
							<CheckSVG
								color='var(--accent-500)'
								width='14'
								height='14'
							/>
							<span>All four corners must be visible</span>
						</li>
						<li className='upload-result__guidance-item'>
							<CheckSVG
								color='var(--accent-500)'
								width='14'
								height='14'
							/>
							<span>Make sure all text and numbers are legible</span>
						</li>
					</ul>
				</div>
			</Hvc>

			{/* Upload component */}
			<MultipleMediaUploadComponent
				setFiles={(files) => {
					updateState('files', files);
				}}
				handlePreviewFile={handlePreviewFile}
				files={files}
				load={busy}
				uploadDocs={onUploadDocument}
				isUpdate={isUpdate}
				isUploaded={isUploaded}
				uploadedFiles={uploadedFiles}
				fileStatuses={fileStatuses}
				onRetry={onRetryFile}
			/>

			{/* Action buttons */}
			<Hvc
				removeDOM
				view={!!files.length}
			>
				<div className='upload-result__actions'>
					{/* Location gate — persistent, not just a transient toast */}
					{gpsBlocked && (
						<div
							className='upload-result__gps-gate'
							role='alert'
						>
							<span className='upload-result__gps-gate-icon'>
								<AlertSVG />
							</span>
							<div className='upload-result__gps-gate-copy'>
								<strong>Location required</strong>
								<p>Enable location access so each result sheet can be geo-stamped before upload.</p>
							</div>
						</div>
					)}

					<TypeButton
						title={
							locating ? 'Getting location…' : batchUploading ? 'Uploading…' : !isUpdate ? 'Upload Results' : 'Update Results'
						}
						buttonType={(isUploaded && !isUpdate) || busy ? 'disabled' : 'bold'}
						disabled={(isUploaded && !isUpdate) || busy}
						buttonSize='small'
						icon={<UploadIconSVG color='#fff' />}
						load={busy}
						className='w-100'
						onClick={onUploadDocument}
					/>

					{isUploaded && (
						<TypeButton
							title='Next — Result Summary'
							buttonSize='small'
							className='w-100'
							buttonType='outlined'
							onClick={() => onStage('Result Summary')}
						/>
					)}
					{preview && (
						<TypeButton
							title='End Preview'
							buttonSize='small'
							className='w-100'
							buttonType='outlined'
							onClick={() => onStage('Preview')}
						/>
					)}
				</div>
			</Hvc>
		</div>
	);
};

export default UploadResultStage;
